import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { FileKey2, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { apiRequest } from '@/lib/queryClient';
import type { UserCertificateView } from '@shared/user-certificate';

export function A1SigningDialog({ open, onOpenChange, onSubmit, rtName, hasCpf }: {
  open: boolean; onOpenChange(open: boolean): void; onSubmit(): Promise<void>;
  rtName: string; hasCpf: boolean;
}) {
  const { profile } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const path = `/api/usuarios/${profile?.id}/certificado-digital`;
  const certificate = useQuery<{ certificate: UserCertificateView | null }>({
    queryKey: [path], queryFn: async () => (await apiRequest('GET', path)).json(),
    enabled: open && !!profile?.id, staleTime: 0,
  });
  const current = certificate.data?.certificate;
  const valid = current && current.matchesCpf && new Date(current.validFrom) <= new Date() && new Date(current.validTo) > new Date();
  useEffect(() => { if (!open) setError(null); }, [open]);
  async function submit() {
    if (busy || !valid || !hasCpf) return;
    setBusy(true); setError(null);
    try { await onSubmit(); onOpenChange(false); }
    catch (cause) { setError(cause instanceof Error ? cause.message : 'Não foi possível emitir o laudo.'); }
    finally { setBusy(false); }
  }
  return <Dialog open={open} onOpenChange={value => { if (!busy) onOpenChange(value); }}>
    <DialogContent className="max-h-[90dvh] overflow-y-auto sm:max-w-lg">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2"><FileKey2 aria-hidden="true" className="h-5 w-5" />Assinar E Emitir Laudo</DialogTitle>
        <DialogDescription>A emissão usa automaticamente o certificado A1 cadastrado no usuário de {rtName}.</DialogDescription>
      </DialogHeader>
      {certificate.isLoading ? <p role="status">Consultando certificado do RT…</p> : certificate.isError ?
        <div className="space-y-3"><p role="alert">Não foi possível consultar o certificado.</p><Button variant="outline" onClick={() => certificate.refetch()}>Tentar novamente</Button></div> : current ?
        <div className="space-y-2 text-sm">
          <p className="font-medium">{current.signerName}</p>
          <p className="break-words text-muted-foreground">{current.issuer}</p>
          <p>Válido até {new Date(current.validTo).toLocaleDateString('pt-BR')}</p>
          {!valid && <p role="alert" className="text-destructive">Certificado fora da validade ou incompatível com o CPF atual. Substitua-o no usuário.</p>}
        </div> : <p role="alert">Cadastre o certificado A1 no usuário do RT antes de emitir.</p>}
      {!hasCpf && <p role="alert" className="text-sm text-destructive">Complete o CPF do RT no perfil.</p>}
      <Link href="/perfil" className="text-sm text-primary underline underline-offset-4">Gerenciar meu certificado</Link>
      {error && <p role="alert" className="whitespace-pre-wrap text-sm text-destructive">{error}</p>}
      {busy && <p role="status" className="text-sm text-muted-foreground">Validando o certificado cadastrado e assinando o PDF…</p>}
      <DialogFooter className="gap-2"><Button variant="outline" disabled={busy} onClick={() => onOpenChange(false)}>Cancelar</Button><Button disabled={busy || !valid || !hasCpf || certificate.isFetching} onClick={submit}>{busy && <Loader2 aria-hidden="true" className="mr-2 h-4 w-4 animate-spin" />}Assinar E Emitir</Button></DialogFooter>
    </DialogContent>
  </Dialog>;
}
